import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Authentication context interface
 */
interface AuthContextType {
  /**
   * Whether the user is authenticated
   */
  isAuthenticated: boolean;
  /**
   * Whether authentication state is being loaded from storage
   */
  isLoading: boolean;
  /**
   * Mark user as authenticated
   */
  authenticate: () => void;
  /**
   * Logout and clear authentication
   */
  logout: () => void;
}

/**
 * Authentication context for passcode-based access control
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Local storage key for authentication state
 */
const AUTH_STORAGE_KEY = 'kxd-authenticated';

/**
 * AuthProvider Props
 */
interface AuthProviderProps {
  /**
   * Child components
   */
  children: ReactNode;
}

/**
 * AuthProvider Component
 * 
 * Provides authentication context to the application.
 * Authentication state is persisted in localStorage to survive page refreshes
 * and synced across browser tabs.
 * 
 * @example
 * // Wrap your app with AuthProvider
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * 
 * @example
 * // Use authentication in components
 * const { isAuthenticated, authenticate, logout } = useAuth();
 * 
 * if (!isAuthenticated) {
 *   return <PasscodePage onAuthenticated={authenticate} />;
 * }
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load authentication state from localStorage on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);

    // Listen for changes in localStorage across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === AUTH_STORAGE_KEY) {
        if (e.newValue === 'true') {
          setIsAuthenticated(true);
        } else if (e.newValue === null || e.newValue === 'false') {
          setIsAuthenticated(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  /**
   * Mark user as authenticated
   */
  const authenticate = (): void => {
    setIsAuthenticated(true);
    localStorage.setItem(AUTH_STORAGE_KEY, 'true');
  };

  /**
   * Logout and clear authentication
   */
  const logout = (): void => {
    setIsAuthenticated(false);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, authenticate, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 * 
 * Custom hook to access authentication context.
 * Must be used within an AuthProvider.
 * 
 * @throws Error if used outside AuthProvider
 * @returns Authentication context with isAuthenticated, authenticate, and logout
 * 
 * @example
 * const { isAuthenticated, authenticate, logout } = useAuth();
 * 
 * if (!isAuthenticated) {
 *   return <PasscodeForm onSubmit={authenticate} />;
 * }
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
